/**
 * Animation event markers on the open clip.
 *
 * Every mutation goes through the shared `clipOps` event functions and rides `commitClipEdit`, so
 * markers undo, redo and mark the clip dirty exactly like keyframes do.
 *
 * The selection is a `(frame, name)` REF that each action re-resolves and hands forward. Events
 * re-sort after every edit — dragging a marker past another one changes both their array
 * positions — so an index-based selection would silently start addressing a different marker
 * mid-drag, and would point into a stale array after `Ctrl+Z`.
 */

import {
  addClipEvent,
  isAppError,
  eventsAtFrame,
  lastLegalEventFrame,
  removeClipEvent,
  updateClipEvent,
} from '../../shared';
import type { ClipEvent, ClipEventData } from '../../shared';
import type { ClipEventRef, EditorState } from '../types';
import { commitClipEdit, reportError, showToast, type ActionContext } from './context';

/** What a fresh marker is called until the user renames it. */
export const DEFAULT_EVENT_NAME = 'event';

export interface EventActions {
  /**
   * Add a marker at `frame`. The name defaults to `DEFAULT_EVENT_NAME`, uniquified against the
   * markers already on that frame so two clicks in the same spot do not collapse into one.
   */
  addEvent(frame: number, name?: string): void;
  removeEvent(ref: ClipEventRef): void;
  /** Move a marker to another frame. Clamped to the clip's legal range. */
  moveEvent(ref: ClipEventRef, frame: number): void;
  renameEvent(ref: ClipEventRef, name: string): void;
  setEventData(ref: ClipEventRef, data: ClipEventData | undefined): void;
  selectEvent(ref: ClipEventRef | null): void;
  /** Delete whatever marker is selected. No-op when nothing is. */
  deleteSelectedEvent(): void;
  /**
   * Bracket a marker drag so the whole gesture is ONE undo entry.
   *
   * A coalesce KEY on the individual edits is not enough — the stack only merges commands while a
   * group is open (`undo.ts`), so without this a drag across forty pixels leaves forty entries and
   * `Ctrl+Z` walks back one mouse-move at a time. Same shape as `beginKeyDrag`/`endKeyDrag`.
   */
  beginEventDrag(): void;
  endEventDrag(): void;
}

const EVENT_DRAG_COALESCE_KEY = 'events:move';

/** The event a ref points at in the clip as it stands now, or `undefined` when it is stale. */
export function resolveEvent(state: EditorState, ref: ClipEventRef | null): ClipEvent | undefined {
  if (!ref || !state.clip) return undefined;
  return state.clip.events?.find((e) => e.frame === ref.frame && e.name === ref.name);
}

function uniqueName(base: string, taken: readonly ClipEvent[]): string {
  const names = new Set(taken.map((e) => e.name));
  if (!names.has(base)) return base;
  for (let n = 2; n < 1000; n++) {
    const candidate = `${base} ${n}`;
    if (!names.has(candidate)) return candidate;
  }
  return `${base} ${Date.now()}`;
}

export function createEventActions(ctx: ActionContext): EventActions {
  const clampToClip = (frame: number): number => {
    const clip = ctx.getState().clip;
    if (!clip) return 0;
    return Math.max(0, Math.min(lastLegalEventFrame(clip), Math.round(frame)));
  };

  /**
   * Local, not `this.removeEvent`: the store wraps every action to notify subscribers and re-sync
   * history, so calling one action through the returned object from inside another would nest a
   * wrapped call inside a wrapped call.
   */
  const removeEvent = (ref: ClipEventRef): void => {
    const state = ctx.getState();
    const clip = state.clip;
    if (!clip) return;

    const next = removeClipEvent(clip, ref.frame, ref.name);
    if (next === clip) return;

    const selected = state.selectedEvent;
    const stillSelected =
      selected && selected.frame === ref.frame && selected.name === ref.name ? null : selected ?? null;
    commitClipEdit(ctx, 'Delete event', next, undefined, undefined, undefined, undefined, stillSelected);
  };

  return {
    addEvent(frame, name) {
      const state = ctx.getState();
      const clip = state.clip;
      if (!clip) return;

      const target = clampToClip(frame);
      // Several events may share a frame, so a repeat click adds another rather than replacing —
      // but it needs its own name, or the two would be the same `(frame, name)` identity and
      // `normalizeEvents` would collapse them into one.
      const finalName = uniqueName((name ?? DEFAULT_EVENT_NAME).trim() || DEFAULT_EVENT_NAME, eventsAtFrame(clip, target));

      try {
        const next = addClipEvent(clip, target, finalName);
        commitClipEdit(ctx, 'Add event', next, undefined, undefined, undefined, undefined, {
          frame: target,
          name: finalName,
        });
      } catch (e) {
        reportError(ctx, isAppError(e) ? e.message : 'Could not add the event.');
      }
    },

    removeEvent,

    moveEvent(ref, frame) {
      const state = ctx.getState();
      const clip = state.clip;
      if (!clip) return;

      const target = clampToClip(frame);
      if (target === ref.frame) return;

      const next = updateClipEvent(clip, ref, { frame: target });
      if (next === clip) return;
      // The ref travels with the marker; a drag issues several of these in a row and each must
      // address where the marker is NOW.
      commitClipEdit(ctx, 'Move event', next, undefined, EVENT_DRAG_COALESCE_KEY, undefined, undefined, {
        frame: target,
        name: ref.name,
      });
    },

    renameEvent(ref, name) {
      const state = ctx.getState();
      const clip = state.clip;
      if (!clip) return;

      const trimmed = name.trim();
      if (trimmed.length === 0) {
        showToast(ctx, 'error', 'An event needs a name.');
        return;
      }
      if (trimmed === ref.name) return;

      try {
        const next = updateClipEvent(clip, ref, { name: trimmed });
        if (next === clip) return;
        commitClipEdit(ctx, 'Rename event', next, undefined, undefined, undefined, undefined, {
          frame: ref.frame,
          name: trimmed,
        });
      } catch (e) {
        reportError(ctx, isAppError(e) ? e.message : 'Could not rename the event.');
      }
    },

    setEventData(ref, data) {
      const clip = ctx.getState().clip;
      if (!clip) return;

      const next = updateClipEvent(clip, ref, { data });
      if (next === clip) return;
      commitClipEdit(ctx, 'Edit event data', next, undefined, undefined, undefined, undefined, ref);
    },

    selectEvent(ref) {
      // Not undoable, exactly like `selectBone`: a selection is workspace state, not clip content.
      ctx.set({ selectedEvent: ref });
    },

    beginEventDrag() {
      ctx.history.beginCoalesce('Move event', EVENT_DRAG_COALESCE_KEY);
    },

    endEventDrag() {
      ctx.history.endCoalesce();
    },

    deleteSelectedEvent() {
      const state = ctx.getState();
      const ref = state.selectedEvent;
      if (!ref || !state.clip) return;
      removeEvent(ref);
    },
  };
}
